/* OfflineDiscoveryUploadAction.js */

"use strict";

const {unzipSync, strFromU8, gunzipSync} = require("fflate");
const {parseTar} = require("nanotar");
const UploadDiscoveryResultsVeilComponent = require("./UploadDiscoveryResultsVeilComponent");
const UploadSummaryVeilComponent = require("./UploadSummaryVeilComponent");

const LOADING_MSG = (isSingle) =>
    `Importing your discovery result ${isSingle ? "file" : "files"}... This could take a moment`;

/**
 * Class representing an action to upload and import offline discovery result files for IoT devices.
 *
 * OfflineDiscoveryUploadAction is responsible for:
 *  - Managing user feedback and UI overlays during the upload/import process.
 *  - Accepting .zip or .tar.gz archives containing IoT discovery results.
 *  - Extracting and validating the contents of uploaded files.
 *  - Invoking backend logic to process discovery results.
 *  - Displaying import summaries or errors to the user.
 *
 * Usage:
 *   const action = new OfflineDiscoveryUploadAction(logger, view, DeviceEntity, userSession, userFeedback, activityMonitor);
 *   action.run();
 */
class OfflineDiscoveryUploadAction {
    /**
     * Create an OfflineDiscoveryUploadAction.
     * @param {Object} logger - Logger instance.
     * @param {Object} activeView - Current active view (UI context).
     * @param {Object} DeviceEntity - Device entity backend handler.
     * @param {Object} userSession - Current user session.
     * @param {Object} userFeedback - Modal user feedback handler.
     * @param {Object} activityMonitor - Activity monitor instance.
     */
    constructor(logger, activeView, DeviceEntity, userSession, userFeedback, activityMonitor) {
        this._logger = logger;
        this._activeView = activeView;
        this._modalUserFeedback = userFeedback;
        this._DeviceEntity = DeviceEntity;
        this._userSession = userSession;
        this._activityMonitor = activityMonitor;
        this.run = this.run.bind(this);
    }

    /**
     * Initiates the upload and import workflow for offline discovery result files.
     * @returns {Promise<void>}
     */
    run() {
        return this._modalUserFeedback
            .displayCustom(new UploadDiscoveryResultsVeilComponent(this._logger), {
                dontHide: true,
            })
            .then(async (files) => {
                if (files) {
                    this._activityMonitor.stopPolling();
                    this._showLoadingVeil(LOADING_MSG(files.length > 1));

                    const discoveries = [];
                    for (const file of Array.from(files)) {
                        const discoveryFilename = file.name;
                        if (!discoveryFilename.startsWith("discover-")) {
                            continue;
                        }

                        let discovery = null;
                        if (file.name.endsWith(".zip")) {
                            discovery = await this._extractDiscoveryFromArchive({
                                file,
                                discoveryFilename,
                                extractEntries: async (file) => {
                                    const arrayBuffer = await file.arrayBuffer();
                                    const uint8 = new Uint8Array(arrayBuffer);
                                    const zipEntries = unzipSync(uint8);
                                    return Object.entries(zipEntries).map(([name, data]) => ({
                                        name,
                                        data,
                                    }));
                                },
                            });
                        } else if (file.name.endsWith(".tar.gz")) {
                            discovery = await this._extractDiscoveryFromArchive({
                                file,
                                discoveryFilename,
                                extractEntries: async (file) => {
                                    const arrayBuffer = await file.arrayBuffer();
                                    const tarData = gunzipSync(new Uint8Array(arrayBuffer));
                                    const entries = await parseTar(tarData);
                                    return entries.map((entry) => ({
                                        name: entry.name,
                                        data: entry.data,
                                    }));
                                },
                            });
                        } else {
                            console.warn(
                                "Unsupported discovery result file type (only .zip and .tar.gz are supported) - Skipping ",
                                file.name
                            );
                            continue;
                        }

                        if (discovery) {
                            discoveries.push(discovery);
                        } else {
                            return Promise.reject(
                                `The file ${file.name} is not a valid discovery result file ` +
                                    `(only .zip and .tar.gz are supported). ` +
                                    `Please check the selected files and try again.`
                            );
                        }
                    }

                    return Promise.resolve(discoveries);
                }

                return Promise.resolve([]);
            })
            .then((discoveries) => {
                if (discoveries.length === 0) {
                    return Promise.reject(
                        "None of the uploaded discovery result files is valid. Please check the selected .zip or .tar.gz files."
                    );
                }

                return this._DeviceEntity.runOdmDiscoveryWork(
                    this._userSession.activeOrganization.id,
                    {},
                    {discoveries}
                );
            })
            .then((importResults) => {
                this._activityMonitor.startPolling();
                return this._modalUserFeedback.displayCustom(
                    new UploadSummaryVeilComponent(importResults, this._logger)
                );
            })
            .catch((error) => {
                this._activityMonitor.startPolling();
                if (error && error.aborted) {
                    this._abortUpload();
                } else if (error && !error.userRejected) {
                    this._modalUserFeedback.displayError({error: {errorMsg: error, errorCode: ""}});
                }
            });
    }

    /**
     * Extracts discovery data from an archive file.
     * @param {Object} params
     * @param {File} params.file - The archive file.
     * @param {string} params.discoveryFilename - File name.
     * @param {Function} params.extractEntries - Function to extract entries from archive.
     * @returns {Promise<Object|null>} Discovery object or null if invalid.
     */
    async _extractDiscoveryFromArchive({file, discoveryFilename, extractEntries}) {
        const discovery = {
            discoveryFilename,
            discovery: null,
            commandLog: null,
        };

        const entries = await extractEntries(file);

        for (const {name, data} of entries) {
            try {
                if (name === "commandLog.json") {
                    discovery.commandLog = JSON.parse(strFromU8(data));
                } else if (name === "discovery.json") {
                    discovery.discovery = JSON.parse(strFromU8(data));
                }
            } catch (e) {
                continue;
            }
        }

        if (!discovery.commandLog || !discovery.discovery) {
            return null;
        }
        return discovery;
    }

    /**
     * Shows a loading veil with a given message.
     * @param {string} message
     * @returns {Promise}
     */
    _showLoadingVeil(message) {
        return this._modalUserFeedback.displayLoading({message, dontHide: true});
    }

    /**
     * Hides the current loading veil.
     * @returns {Promise}
     */
    _hideLoadingVeil() {
        return this._modalUserFeedback.hideDisplayed();
    }

    /**
     * Cleans up any resources and hides UI overlays.
     */
    shutdown() {
        this._modalUserFeedback = undefined;
        this._logger = undefined;
        this._activeView = undefined;
        this._DeviceEntity = undefined;
        this._userSession = undefined;
        this._activityMonitor = undefined;
    }

    /**
     * Aborts the current upload process and hides overlays.
     * @returns {Promise}
     */
    _abortUpload() {
        console.log("OfflineDiscoveryUploadAction.run() - Aborting...");
        return this._modalUserFeedback.hideDisplayed();
    }
}

module.exports = OfflineDiscoveryUploadAction;

OfflineDiscoveryUploadAction.$inject = [
    "$teve.logger",
    "gameEditor.activeView",
    "$teve.entities.Device",
    "$teve.userSession",
    "$teve.ui.userFeedback",
    "$teve.activityMonitor",
];

OfflineDiscoveryUploadAction.$displayDetails = {
    type: "Action",
    name: "OfflineDiscoveryUploadAction",
    icon: "devicemanager.offline_discovery_upload",
    shown: true,
    title: "Import discovery results",
    order: 2,
};
